import {
  Breadcrumb,
  Card,
  CardStandardHeader,
} from "@coherence-design-system/controls";
import {
  IIconProps,
  FontSizes,
  FontWeights,
  ActionButton,
  MessageBarType,
  MessageBar,
  Separator,
  IBreadcrumbItem,
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { Stack } from "@fluentui/react/lib/Stack";
import React, { useContext, useEffect, useState } from "react";
import { ISearchFor } from "../Common/Interfaces/ISearchFor";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DeliveryOptions,
  DeliveryReasons,
  EDIT_ACTION,
  ErrorsDefaultState,
  InitialQuotationInfo,
  InitialStateOfResponseMessage,
  NO,
  PaymentMethods,
  PaymentOptions,
  QuotationStatus,
  YES,
} from "../Common/QuotationData";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationDetailService } from "../../../services/Quotations/QuotationDetailService";
import {
  columnHeaderCardTokenProps,
  stackTokensWithGap20,
  stackTokensWithGap30,
} from "../QuotationStyle";
import { CustomCardStyle, columnHeaderCardProps } from "./QuotationDetailStyle";
import { QuotationContext } from "../QuotationContext";
import {
  IQuotationType,
  IResponseMessage,
  QuotationInfoType,
} from "../Quotation.types";
import ItemComponent from "../Components/ItemComponent";
import { LoadingState } from "@coherence-design-system/loading-state";
import { QuotationService } from "../../../services/Quotations/QuotationService";
import { SignQuotationComponent } from "../Components/SignQuotationComponent/SignQuotationComponent";
import { DialogComponent } from "../Components/DialogComponent";
import { BreadcrumbStyle } from "../AddQuotation/AddQuotationStyle";
import { stackTokensWithGap10 } from "../../CountriesProfile/CountriesProfile.type";
import { useLocalStorageContext } from "../../../Provider/LocalStorageProvider";
import { UserRoles } from "../../../common/enum/UserRoles";
import { useDateFormatter } from "../../../Utils/DateFormatter";
import { formatDateWithPattern } from "../../../Utils/DateFormatUtility";
import {
  priceWithCurrency,
  symbolForCurrency,
} from "../../../Utils/PriceWithCurrency";
import { QuoteSessionTracker } from "../../../Utils/QuoteSessionTracker";
import { ActivityTimingTracker } from "../../../Utils/ActivityTimingTracker";
import { AddPaymentComponent } from "../Components/AddPaymentComponent/AddPaymentComponent";
import { PaymentComponent } from "../Components/PaymentComponent/PaymentComponent";
const EditIcon: IIconProps = { iconName: "Edit" };
const SignatureIcon: IIconProps = { iconName: "InsertSignatureLine" };
const CancelIcon: IIconProps = { iconName: "ErrorBadge" };
const PreviewIcon: IIconProps = { iconName: "Preview" };
const DownloadDocumentIcon: IIconProps = { iconName: "DownloadDocument" };
const GlobalIcon: IIconProps = { iconName: "Globe" };
export const QuotationDetail: React.FunctionComponent = (prop) => {
  const appInsights = useAppInsightsContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, selectedLanguage, setUser } = useLocalStorageContext();
  const SearchFor: ISearchFor = location.state as ISearchFor;
  const [quotationStatusMessage, setQuotationStatusMessage] = useState(false);
  const [quotationStatusFailedMessage, setQuotationStatusFailedMessage] =
    useState(false);
  const [quotationSuccessMessage, setQuotationSuccessMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationFailureMessage, setQuotationFailureMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  const { setQuotationError } = useContext(QuotationContext) as IQuotationType;
  const [isLoading, setIsLoading] = useState(false);
  const [zoomPercentage, setZoomPercentage] = useState<number>(100);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isPanelOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);
  const [isOpenModal, { setTrue: openModal, setFalse: closeModal }] =
    useBoolean(false);
  const [isEditPanelOpen, { setTrue: openEditPanel, setFalse: dismissEditPanel }] =
    useBoolean(false);
  const [updatedStatus, setUpdatedStatus] = useState<string>("");
  const getQuotationDetail = () => {
    QuotationDetailService.getQuotationDetail(
      SearchFor !== undefined ? SearchFor : { searchText: "", partitionKey: "" }
    )
      .then((res) => {
        appInsights.trackEvent({
          name: "In QuotationDetail.tsx, quote details fetched successfully.",
          properties: { quotationId: res.data.data.quotationId },
        });
        const { data } = res.data;
        setQuotationInfo({
          ...quotationInfo,
          quotationId: data.quotationId,
          partitionKey: data.partitionKey,
          quotationNumber: data.quotationNumber,
          quotationName: data.quotationName,
          quotationDescription: data.quotationDescription,
          quotationDate: data.quotationDate,
          expiryDate: data.expiryDate,
          customerRequestedPromiseDate: data.customerRequestedPromiseDate,
          total: data.total,
          customer: data.customer,
          status: data.status,
          msxOpportunityId: data.msxOpportunityId,
          paymentType: data.paymentType,
          selectedPaymentType: data.selectedPaymentType,
          paymentMethod: data.paymentMethod,
          selectedPaymentMethod: data.selectedPaymentMethod,
          paymentTerms: data.paymentTerms,
          selectedPaymentTerms: data.selectedPaymentTerms,
          adyenPaymentLink: data.adyenPaymentLink,
          customerPurchaseOrder: data.customerPurchaseOrder,
          deliveryMode: data.deliveryMode,
          selectedDeliveryMode: data.selectedDeliveryMode,
          deliveryReason: data.deliveryReason,
          selectedDeliveryReason: data.selectedDeliveryReason,
          quoteCurrency: data.quoteCurrency,
          comments: data.comments,
          microsoftAccountLead: data.microsoftAccountLead,
          microsoftAccountPhone: data.microsoftAccountPhone,
          microsoftAccountEmail: data.microsoftAccountEmail,
          preparedBy: data.createdByName,
          country: data.country,
          quoteLineItems: data.itemDetail,
          quoteLineItemsClone: data.itemDetail,
          bundles: data.bundles ?? [],
          bundleItems: data.bundleItems ?? [],
          standaloneItems: data.standaloneItems ?? [],
          subTotal: data.subTotal,
          totalTax: data.totalTax,
          totalFee: data.totalFee,
        });

        // Reset language when opening any quotation
        // For Japan: CENOPS and GTFS roles default to English; other roles default to Japanese
        // For France: All roles default to French
        // All other countries: Default to English
        // All roles can still switch language using the language switcher button
        const currentUserData = JSON.parse(
          localStorage.getItem("user") || "{}"
        );
        
        // Determine default language based on country and role
        let defaultLanguage = "en";
        if (country?.toLowerCase() === "japan") {
          defaultLanguage = (userRole === UserRoles.CENOPS || userRole === UserRoles.GTFS) ? "en" : "ja";
        } else if (country?.toLowerCase() === "france") {
          defaultLanguage = "fr"; // Default to French for all roles in France
        }
        // For all other countries, defaultLanguage remains "en"
        
        const updatedUser = {
          ...currentUserData,
          selectedLanguage: defaultLanguage,
        };
        setUser(updatedUser);
        localStorage.setItem("selectedLanguage", defaultLanguage);

        appInsights.trackEvent({
          name: `In QuotationDetail.tsx, Reset language for ${country} quotation based on user role.`,
          properties: {
            quotationId: data.quotationId,
            country: data.country,
            userRole: userRole,
            defaultLanguage: defaultLanguage,
            previousLanguage: currentUserData.selectedLanguage || "unknown",
          },
        });

        setIsLoading(false);
      })
      .catch((error) => {
        appInsights.trackException({
          error,
          properties: {
            name: "In QuotationDetail.tsx, error while getting searched quotation details.",
          },
        });
        setIsLoading(false);
      });
  };
  useEffect(() => {
    setIsLoading(true);
    getQuotationDetail();
  }, [SearchFor.searchText, SearchFor.partitionKey]);

  const {
    quotationNumber,
    quotationDate,
    expiryDate,
    total,
    status,
    msxOpportunityId,
    quoteCurrency,
    customerRequestedPromiseDate,
    microsoftAccountLead,
    microsoftAccountPhone,
    microsoftAccountEmail,
    preparedBy,
    comments,
    bundleItems,
    standaloneItems,
    country,
    customer: {
      name,
      taxExempt,
      authorizedBuyerFirstName,
      authorizedBuyerLastName,
      authorizedBuyerPhone,
      authorizedEmailId,
      businessAddress,
      selectedBillToAddress,
      selectedShipToAddress,
      selectedBillToContact,
      selectedShipToContact,
    },
  } = quotationInfo;

  const _OnClickQuotationUpdate = () => {
    const { quotationId, partitionKey } = quotationInfo;
    
    // Start quote session tracking for edit
    QuoteSessionTracker.startSession();
    // Clear any previous activity timing data when starting new quote session
    ActivityTimingTracker.clearTimings();
    console.log('🆕 Started new quote edit session - cleared previous activity timing data');
    
    appInsights.trackEvent({
      name: "In QuotationDetail.tsx, Clicked Update Quotation button to update selected Quotation.",
      properties: { quotationNumber: quotationInfo.quotationNumber },
    });
    setQuotationInfo({
      ...InitialQuotationInfo,
      quoteLineItems: [],
      quoteLineItemsClone: [],
      standaloneItems: [],
      standaloneItemsClone: [],
      bundles: [],
      bundleItems: [],
      bundleItemsClone: [],
    });
    setQuotationError({
      ...ErrorsDefaultState,
    });
    navigate("/EditQuotation", {
      state: {
        actionName: EDIT_ACTION,
        quotationId,
        partitionKey,
      },
    });
  };

  const _OnClickQuotePreview = () => {
    appInsights.trackEvent({
      name: "In ItemComponent.tsx, Clicked Quotation Invoice to generate invoice of selected Quotation.",
      properties: { quotationNumber: quotationInfo.quotationNumber },
    });
    navigate("/QuotationInvoice", { state: quotationInfo });
  };

  const _OnClickDPRProforma = () => {
    appInsights.trackEvent({
      name: "In UpdateItemsComponent.tsx, Clicked Quotation Invoice to generate invoice of selected Quotation.",
      properties: { quotationNumber: quotationInfo.quotationNumber },
    });
    
    if (quotationInfo.paymentMethod === PaymentMethods[0].key) {
        openEditPanel();
    } else {
      navigate("/DPRProforma", { state: quotationInfo });
    }
  };

  const _OnClickLanguageSwitch = () => {
    let newLanguage = "en";
    
    if (country?.toLowerCase() === "japan") {
      newLanguage = selectedLanguage === "ja" ? "en" : "ja";
    } else if (country?.toLowerCase() === "france") {
      newLanguage = selectedLanguage === "fr" ? "en" : "fr";
    }

    appInsights.trackEvent({
      name: `In QuotationDetail.tsx, Switched language for ${country} quotation.`,
      properties: { 
        quotationNumber: quotationInfo.quotationNumber,
        fromLanguage: selectedLanguage,
        toLanguage: newLanguage,
        country: country
      },
    });

    // Get all current user data and update only the language
    const currentUserData = JSON.parse(localStorage.getItem("user") || "{}");
    const updatedUser = {
      ...currentUserData,
      selectedLanguage: newLanguage
    };

    setUser(updatedUser);
    localStorage.setItem("selectedLanguage", newLanguage);
  };

  const _OnClickUpdateQuotationStatus = (quoteStatus: string) => {
    const { quotationId, partitionKey, total } = quotationInfo;
    appInsights.trackEvent({
      name: `In QuotationDetail.tsx, Clicked ${quoteStatus} Quotation button to Cancel selected Quotation.`,
      properties: { quotationNumber: quotationInfo.quotationNumber },
    });
    if (
      (quoteStatus === QuotationStatus.Confirmed && total) ||
      quoteStatus === QuotationStatus.Cancelled
    ) {
      QuotationService.updateQuotationStatus(
        quotationId,
        partitionKey,
        quoteStatus
      )
        .then((res) => {
          if (res.data.code === 200) {
            appInsights.trackEvent({
              name: "In QuotationDetail.tsx, quotation status has been updated successfully.",
              properties: { status: status },
            });
            setQuotationInfo({
              ...quotationInfo,
              ...res.data.data,
            });
            setQuotationSuccessMessage({
              message: `Quotation has been ${quoteStatus} successfully`,
              showMessage: true,
            });
          } else {
            appInsights.trackTrace({
              message:
                "In QuotationDetail.tsx, something went wrong while updating quotation status.",
            });
            setQuotationInfo({
              ...quotationInfo,
              ...res.data.data,
            });
            setQuotationFailureMessage({
              message: res.data.message,
              showMessage: true,
            });
          }
        })
        .catch((error) => {
          setQuotationFailureMessage({
            message: `${quoteStatus} quotation process has been failed due to some issue.`,
            showMessage: true,
          });
          appInsights.trackException({
            error,
            properties: {
              name: "In QuotationDetail.tsx, error while updating quotation status.",
            },
          });
        });
    } else {
      setQuotationFailureMessage({
        message: `${quoteStatus} quotation should contain at least 1 or more line items.`,
        showMessage: true,
      });
    }
  };

  const getQuoteDeliveryOption = (): string | undefined => {
    return DeliveryOptions.find(
      (x: any) => x.key === quotationInfo.deliveryMode
    )?.text;
  };

  const languageSuffix = 
    country?.toLowerCase() === "japan" ? ` (${selectedLanguage === "ja" ? "日本語" : "English"})` :
    country?.toLowerCase() === "france" ? ` (${selectedLanguage === "fr" ? "Français" : "English"})` : "";

  const getQuoteDeliveryReason = (): string | undefined => {
    return DeliveryReasons.find(
      (x: any) => x.key === quotationInfo.deliveryReason
    )?.text;
  };

  const getQuotePaymentType = (): string | undefined => {
    return PaymentOptions.find((x: any) => x.key === quotationInfo.paymentType)
      ?.text;
  };

  const getBreadcrumbItems = () => {
    const items: IBreadcrumbItem[] = [
      {
        text: "Quotations",
        key: "Quotations",
        onClick: () => {
          navigate("/AllQuotations");
        },
      },
      { text: `Quotation Detail`, key: `Quotation Detail` },
    ];
    if (quotationInfo?.quotationNumber) {
      items.push({
        text: quotationNumber,
        key: quotationNumber,
        isCurrentItem: true,
      });
    }
    return items;
  };
  const cardQuoteHeaderProps = {
    cardTitle: "Quote Details",
  };

  const cardCustomerHeaderProps = {
    cardTitle: "Customer Account Details",
  };

  const { formatDate } = useDateFormatter(quotationInfo?.country);
  // Helper to safely format date values (handles ISO/UTC and empty/null)

  function safeFormatDate(dateValue: string | Date | undefined | null, formatDate: (d: Date | string) => string) {
    if (!dateValue) return "";
    if (dateValue instanceof Date) {
      try {
        return formatDate(dateValue);
      } catch (e) {
        return formatDateWithPattern(dateValue, "DD-MM-YYYY");
      }
    }
    const parsed = new Date(dateValue);
    if (!isNaN(parsed.getTime())) {
      try {
        return formatDate(parsed);
      } catch (e) {
        return formatDateWithPattern(parsed, "DD-MM-YYYY");
      }
    }
    try {
      return formatDate(dateValue);
    } catch (e) {
      // fallback to default format if all else fails
      try {
        return formatDateWithPattern(new Date(dateValue), "DD-MM-YYYY");
      } catch {
        return String(dateValue);
      }
    }
  }

  useEffect(() => {
    const updateZoomPercentage = () => {
      const zoomLevel = (window.outerWidth / window.innerWidth) * 100;
      setZoomPercentage(zoomLevel);
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateZoomPercentage();

    window.addEventListener("resize", updateZoomPercentage);

    return () => {
      window.removeEventListener("resize", updateZoomPercentage);
    };
  }, []);
  return (
    <Stack tokens={stackTokensWithGap20}>
      {quotationSuccessMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() =>
            setQuotationSuccessMessage({
              ...quotationSuccessMessage,
              showMessage: false,
            })
          }
        >
          {quotationSuccessMessage.message}
        </MessageBar>
      )}
      {quotationFailureMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() =>
            setQuotationFailureMessage({
              ...quotationFailureMessage,
              showMessage: false,
            })
          }
        >
          {quotationFailureMessage.message}
        </MessageBar>
      )}
      {quotationStatusMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setQuotationStatusMessage(false)}
        >
          Quotation status has been updated successfully
        </MessageBar>
      )}
      {quotationStatusFailedMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setQuotationStatusFailedMessage(false)}
        >
          Unable to update the quotation status. Please try again later.
        </MessageBar>
      )}
      {isLoading ? (
        <div
          style={{
            height: "500px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingState
            primaryText="Loading Quotation Detail Page"
            styles={{}}
          />
        </div>
      ) : (
        <>
          <div>
            <Breadcrumb
              items={getBreadcrumbItems()}
              style={{ marginLeft: "1%", marginBottom: "1%" }}
              styles={BreadcrumbStyle}
            />
            {isOpenModal && (
              <DialogComponent
                isOpenModal
                closeModal={closeModal}
                title={`Confirm Quote Cancellation`}
                description={`Are you sure you want to cancel the selected quote ${quotationInfo.quotationNumber}?`}
                yesButtonLabel="Yes"
                noButtonLabel="No"
                onClickYes={() =>
                  _OnClickUpdateQuotationStatus(QuotationStatus.Cancelled)
                }
                onClickNo={closeModal}
              />
            )}
            {isEditPanelOpen && (
              <PaymentComponent
                isEditPanelOpen
                dismissEditPanel={dismissEditPanel}
              />
            )}
            {isPanelOpen && (
              <SignQuotationComponent
                isPanelOpen={isPanelOpen}
                dismissPanel={dismissPanel}
                quotationId={quotationInfo.quotationId}
                quotationNumber={quotationInfo.quotationNumber}
                partitionKey={quotationInfo.partitionKey}
                customerName={quotationInfo.customer.name}
                createdAt={quotationInfo.quotationDate}
                expireAt={quotationInfo.expiryDate}
                paymentType={quotationInfo.paymentType}
                customerPurchaseOrderNumber={
                  quotationInfo.customerPurchaseOrder
                }
                setUpdatedStatus={setUpdatedStatus}
                setQuotationErrorMessage={setQuotationFailureMessage}
                standaloneItems={quotationInfo.standaloneItems}
                bundleItems={quotationInfo.bundleItems}
              />
            )}

            <div
              style={{
                marginLeft: "30px",
                marginRight: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {quotationNumber && name && (
                <h1
                  style={{
                    fontSize: FontSizes.size24,
                    marginTop: "10px",
                    marginBottom: "10px",
                    fontWeight: FontWeights.regular,
                  }}
                >
                  {quotationNumber} - {name}
                </h1>
              )}

              {total && (
                <p
                  style={{
                    marginLeft: "20px",
                    fontSize: FontSizes.size24,
                    fontWeight: FontWeights.bold,
                    marginTop: "10px",
                    marginBottom: "10px",
                    float: "right",
                  }}
                >
                  {`Total: ${priceWithCurrency(
                    quotationInfo.quoteCurrency,
                    total
                  )}`}
                </p>
              )}
            </div>

            <div style={{ marginLeft: "50px", marginRight: "50px" }}>
              {(standaloneItems.length > 0 || bundleItems.length > 0) &&
                (status === QuotationStatus.Draft ||
                  status === QuotationStatus.Confirmed) && (
                  <>
                    <ActionButton
                      iconProps={PreviewIcon}
                      onClick={_OnClickQuotePreview}
                    >
                      Preview Quote{languageSuffix}
                    </ActionButton>
                    {quotationInfo.paymentType === PaymentOptions[0].key && (
                      <ActionButton
                        iconProps={DownloadDocumentIcon}
                        onClick={_OnClickDPRProforma}
                        disabled={
                          userRole === UserRoles.CENOPS || 
                          userRole === UserRoles.Advocate || 
                          (status === QuotationStatus.Draft && 
                            (userRole === UserRoles.GTFS))
                        }
                      >
                        DPR Proforma{languageSuffix}
                      </ActionButton>
                    )}
                    {/* Language Switcher - For Japan and France */}
                    {(country?.toLowerCase() === "japan" || country?.toLowerCase() === "france") && (
                      <ActionButton
                        iconProps={GlobalIcon}
                        onClick={_OnClickLanguageSwitch}
                      >
                        {country?.toLowerCase() === "japan" 
                          ? (selectedLanguage === "ja" ? "日本語" : "English")
                          : (selectedLanguage === "fr" ? "Français" : "English")
                        }
                      </ActionButton>
                    )}
                  </>
                )}

              {quotationInfo && userRole !== UserRoles.GTFS && (
                <>
                  <ActionButton
                    iconProps={EditIcon}
                    onClick={_OnClickQuotationUpdate}
                    disabled={
                      isLoading ||
                      updatedStatus ||
                      status !== QuotationStatus.Draft
                        ? true
                        : false
                    }
                  >
                    Edit
                  </ActionButton>
                  <ActionButton
                    iconProps={SignatureIcon}
                    onClick={() => {
                      if (
                        (standaloneItems.length > 0 ||
                          bundleItems.length > 0) &&
                        parseFloat(quotationInfo.total)
                      ) {
                        openPanel && openPanel();
                      } else {
                        setQuotationFailureMessage({
                          message: `${quotationInfo.quotationNumber} quotation should contain at least 1 or more line items, having order amount greater than zero.`,
                          showMessage: true,
                        });
                      }
                    }}
                    disabled={
                      isLoading ||
                      updatedStatus ||
                      status !== QuotationStatus.Draft
                        ? true
                        : false
                    }
                  >
                    Sign
                  </ActionButton>
                  <ActionButton
                    iconProps={CancelIcon}
                    onClick={() => openModal()}
                    disabled={
                      isLoading ||
                      updatedStatus ||
                      status !== QuotationStatus.Draft
                        ? true
                        : false
                    }
                  >
                    Cancel
                  </ActionButton>
                  <Separator></Separator>
                </>
              )}
              <Card
                styles={
                  zoomPercentage > 100 ||
                  (windowSize.width <= 420 && windowSize.height <= 650)
                    ? CustomCardStyle
                    : undefined
                }
                header={<CardStandardHeader {...cardQuoteHeaderProps} />}
              >
                <Stack horizontal horizontalAlign="space-between">
                  <Stack
                    verticalFill
                    verticalAlign="start"
                    tokens={stackTokensWithGap30}
                  >
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Effective Date</b>
                      </span>
                      <span>{safeFormatDate(quotationDate, formatDate)}</span>
                    </Stack>
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Delivery Reason</b>
                      </span>
                      <span>{getQuoteDeliveryReason()}</span>
                    </Stack>

                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Account Lead</b>
                      </span>
                      <span>{microsoftAccountLead}</span>
                    </Stack>
                  </Stack>
                  <Stack
                    verticalFill
                    verticalAlign="start"
                    tokens={stackTokensWithGap30}
                  >
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Expiration Date</b>
                      </span>
                      <span>{safeFormatDate(expiryDate, formatDate)}</span>
                    </Stack>
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Payment Type</b>
                      </span>
                      <span>{getQuotePaymentType()}</span>
                    </Stack>

                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Account Phone</b>
                      </span>
                      <span>{microsoftAccountPhone}</span>
                    </Stack>
                  </Stack>
                  <Stack
                    verticalFill
                    verticalAlign="start"
                    tokens={stackTokensWithGap30}
                  >
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>MSX Opportunity ID</b>
                      </span>
                      <span>{msxOpportunityId}</span>
                    </Stack>

                    {quotationInfo.paymentType === PaymentOptions[1].key ? (
                      <Stack {...columnHeaderCardTokenProps}>
                        <span>
                          <b>Payment Terms</b>
                        </span>
                        <span>{quotationInfo?.paymentTerms}</span>
                      </Stack>
                    ) : (
                      <Stack {...columnHeaderCardTokenProps}>
                        <span>
                          <b>Payment Method</b>
                        </span>
                        <span>{quotationInfo?.paymentMethod}</span>
                      </Stack>
                    )}

                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Account Email</b>
                      </span>
                      <span>{microsoftAccountEmail}</span>
                    </Stack>
                  </Stack>
                  <Stack
                    verticalFill
                    verticalAlign="start"
                    tokens={stackTokensWithGap30}
                  >
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Ship To Country</b>
                      </span>
                      <span>{selectedShipToAddress?.country}</span>
                    </Stack>

                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>PO Number</b>
                      </span>
                      <span>
                        {quotationInfo?.customerPurchaseOrder ? (
                          quotationInfo.customerPurchaseOrder
                        ) : (
                          <br />
                        )}
                      </span>
                    </Stack>

                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Comments</b>
                      </span>
                      <span style={{ maxHeight: "150px", overflow: "auto" }}>
                        {comments}
                      </span>
                    </Stack>
                  </Stack>
                  <Stack
                    verticalFill
                    verticalAlign="start"
                    tokens={stackTokensWithGap30}
                  >
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Quote Currency</b>
                      </span>
                      <span>
                        {`${quoteCurrency} ${symbolForCurrency(
                          quotationInfo.quoteCurrency
                        )}`}
                      </span>
                    </Stack>
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Customer Requested Promise Date</b>
                      </span>
                      <span>{safeFormatDate(customerRequestedPromiseDate, formatDate)}</span>
                    </Stack>
                  </Stack>
                  <Stack
                    verticalFill
                    verticalAlign="start"
                    tokens={stackTokensWithGap30}
                  >
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Delivery Mode</b>
                      </span>
                      <span>{getQuoteDeliveryOption()}</span>
                    </Stack>
                    <Stack {...columnHeaderCardTokenProps}>
                      <span>
                        <b>Prepared By</b>
                      </span>
                      <span>{preparedBy}</span>
                    </Stack>
                  </Stack>
                </Stack>
              </Card>
              <br />
              <br />

              <Card
                styles={
                  zoomPercentage > 100 ||
                  (windowSize.width <= 420 && windowSize.height <= 650)
                    ? CustomCardStyle
                    : undefined
                }
                header={<CardStandardHeader {...cardCustomerHeaderProps} />}
              >
                <Stack
                  horizontal
                  horizontalAlign={
                    zoomPercentage > 100 ||
                    (windowSize.width <= 420 && windowSize.height <= 650)
                      ? "space-between"
                      : "space-evenly"
                  }
                  tokens={stackTokensWithGap10}
                >
                  <Stack {...columnHeaderCardProps}>
                    <span>
                      <b>Sold To</b>
                    </span>
                    <span>
                      <p>{businessAddress?.street}</p>
                      <p>
                        {`${businessAddress?.city}, ${businessAddress?.state} ${businessAddress?.postalCode}`}
                      </p>
                      <p>{businessAddress?.country}</p>
                    </span>
                  </Stack>
                  <Stack {...columnHeaderCardProps}>
                    <span>
                      <b>Sold To Contact</b>
                    </span>
                    <span>
                      <p>{`${authorizedBuyerFirstName} ${authorizedBuyerLastName}`}</p>
                      <p>{authorizedBuyerPhone}</p>
                      <p>{authorizedEmailId}</p>
                    </span>
                  </Stack>
                  <Stack {...columnHeaderCardProps}>
                    <span>
                      <b>Bill To</b>
                    </span>
                    <span>
                      <p>{selectedBillToAddress?.street}</p>
                      <p>
                        {`${selectedBillToAddress?.city}, ${selectedBillToAddress?.state} ${selectedBillToAddress?.postalCode}`}
                      </p>
                      <p>{selectedBillToAddress?.country}</p>
                    </span>
                  </Stack>
                  {selectedBillToContact && (
                    <Stack {...columnHeaderCardProps}>
                      <span>
                        <b>Bill To Contact</b>
                      </span>
                      <span>
                        <p>
                          {`${selectedBillToContact?.contactFirstName}
                          ${selectedBillToContact.contactLastName}`}
                        </p>
                        <p>{selectedBillToContact?.contactPhone}</p>
                        <p>{selectedBillToContact?.contactEmail}</p>
                      </span>
                    </Stack>
                  )}
                  <Stack {...columnHeaderCardProps}>
                    <span>
                      <b>Ship To</b>
                    </span>
                    <span>
                      <p>{selectedShipToAddress?.street}</p>
                      <p>
                        {`${selectedShipToAddress?.city}, ${selectedShipToAddress?.state} ${selectedShipToAddress?.postalCode}`}
                      </p>
                      <p>{selectedShipToAddress?.country}</p>
                    </span>
                  </Stack>
                  {selectedShipToContact && (
                    <Stack {...columnHeaderCardProps}>
                      <span>
                        <b>Ship To Contact</b>
                      </span>
                      <span>
                        <p>
                          {`${selectedShipToContact?.contactFirstName}
                          ${selectedShipToContact.contactLastName}`}
                        </p>
                        <p>{selectedShipToContact?.contactPhone}</p>
                        <p>{selectedShipToContact?.contactEmail}</p>
                      </span>
                    </Stack>
                  )}
                  <Stack {...columnHeaderCardProps}>
                    <span>
                      <b>Tax Exempt</b>
                    </span>
                    <span>{taxExempt ? YES : NO}</span>
                  </Stack>
                </Stack>
              </Card>
              <br />
              <br />
              <Stack>
                <ItemComponent
                  setQuotationMessage={setQuotationStatusMessage}
                  setQuotationErrorMessage={setQuotationStatusFailedMessage}
                  zoomPercentage={zoomPercentage}
                  windowSize={windowSize}
                />
              </Stack>
            </div>
          </div>
        </>
      )}
    </Stack>
  );
};
